import React, { useMemo } from 'react';
import FundingReportChartCard, { FundingReportBarChart } from './FundingReportChartCard';

const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);

const FundingPipelineReport = ({ data, loading }) => {
  const stageItems = useMemo(() => Object.entries(data?.dealsByStage || {}).map(([label, value]) => ({ label, value })), [data]);
  const amountItems = useMemo(() => Object.entries(data?.amountByStage || {}).map(([label, value]) => ({ label, value: Math.round(value || 0) })), [data]);

  if (loading) return <p className="text-sm text-[var(--fg-muted)]">Loading pipeline report...</p>;
  if (!data) return <p className="text-sm text-[var(--fg-muted)]">No pipeline data available.</p>;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[var(--fg-line)] bg-white px-4 py-3 text-sm dark:bg-slate-950/20">
        Funded conversion rate: <strong>{data.fundedConversionRate ?? 0}%</strong> estimated
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <FundingReportChartCard title="Deals by stage" subtitle="Pipeline distribution">
          <FundingReportBarChart items={stageItems} />
        </FundingReportChartCard>
        <FundingReportChartCard title="Amount by stage" subtitle="Estimated approved/funded amounts">
          <FundingReportBarChart items={amountItems} />
        </FundingReportChartCard>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <FundingReportChartCard title="Upcoming close dates" subtitle="Next expected closes">
          {(data.upcomingCloseDates || []).length === 0 ? (
            <p className="text-sm text-[var(--fg-muted)]">No upcoming close dates in range.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {data.upcomingCloseDates.map((row) => (
                <div key={row.id} className="flex justify-between rounded-lg bg-white px-3 py-2 dark:bg-slate-950/20">
                  <span>{row.clientName} · {row.lenderName}</span>
                  <span className="text-[var(--fg-muted)]">{row.closeDate}</span>
                </div>
              ))}
            </div>
          )}
        </FundingReportChartCard>
        <FundingReportChartCard title="Overdue next tasks" subtitle="Pipeline actions past due">
          {(data.overdueNextTasks || []).length === 0 ? (
            <p className="text-sm text-[var(--fg-muted)]">No overdue tasks.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {data.overdueNextTasks.map((row) => (
                <div key={row.id} className="rounded-lg bg-white px-3 py-2 dark:bg-slate-950/20">
                  <div className="font-medium">{row.nextTask}</div>
                  <div className="text-xs text-[var(--fg-muted)]">{row.clientName} · due {row.nextTaskDueAt}</div>
                </div>
              ))}
            </div>
          )}
        </FundingReportChartCard>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[var(--fg-line)] text-[var(--fg-muted)]">
              <th className="px-3 py-2">Stage</th>
              <th className="px-3 py-2">Deals</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Avg Probability</th>
            </tr>
          </thead>
          <tbody>
            {stageItems.map((item) => (
              <tr key={item.label} className="border-b border-[var(--fg-line)]/70">
                <td className="px-3 py-2">{item.label}</td>
                <td className="px-3 py-2">{item.value}</td>
                <td className="px-3 py-2">{formatCurrency(data.amountByStage?.[item.label])}</td>
                <td className="px-3 py-2">{data.averageProbabilityByStage?.[item.label] ?? 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FundingPipelineReport;
